import { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { FiDownload, FiUserPlus, FiBarChart, FiTarget, FiActivity, FiZap, FiAward, FiClock, FiAlertTriangle, FiUsers, FiTrendingUp, FiDatabase } from 'react-icons/fi';
import Logo from '../common/Logo';
import UserProfileDropdown from '../Dashboard/UserProfileDropdown';

const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

const AdminHeader = () => (
  <div className="flex items-center pt-4 mb-6">
    <div className="flex-1" />
    <h1 className="text-3xl font-bold flex items-center gap-3">
      <Logo size={28} />
      <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
        AlphaNumbers Admin Portal
      </span>
    </h1>
    <div className="flex-1 flex justify-end">
      <UserProfileDropdown />
    </div>
  </div>
);

export const AdminPage = () => {
  const { user: currentUser } = useAuth();

  const isAdmin = useMemo(() =>
    currentUser?.role === 'admin',
    [currentUser?.role]
  );

  // Early return if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
      <div className="max-w-6xl mx-auto">
        <AdminHeader />

        {/* Analytics & Reports */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Analytics & Reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a href="/admin/progress" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Combined Insights</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Progress Dashboard
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-teal-100">
                      <FiActivity className="w-6 h-6 text-teal-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/caliper" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Learning Analytics</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Caliper Metrics
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-indigo-100">
                      <FiBarChart className="w-6 h-6 text-indigo-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/assessment-results" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Performance Data</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Assessment Results
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-red-100">
                      <FiTarget className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/learning-speed" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Learning Insights</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Learning Speed
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-purple-100">
                      <FiZap className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/learning-speed-v2" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Advanced Analytics</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Learning Speed v2
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-pink-100">
                      <FiZap className="w-6 h-6 text-pink-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/aggregate-time-spent" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Time Tracking</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Aggregate Time Spent
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-teal-100">
                      <FiClock className="w-6 h-6 text-teal-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/grade-mastery" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Mastery Achievement</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Grade Mastery Report
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-yellow-100">
                      <FiAward className="w-6 h-6 text-yellow-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/student-cqpm" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Student Analytics</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Student CQPM Analytics
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-blue-100">
                      <FiBarChart className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/alerts" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Student Monitoring</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Student Alerts
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-orange-100">
                      <FiAlertTriangle className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/student-overview" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Unified View</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Student Overview
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-purple-100">
                      <FiUsers className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/app-performance" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Efficiency Metric</p>
                      <p className="text-lg font-semibold text-gray-900">
                        App Performance
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-100">
                      <FiTrendingUp className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/query-builder" className="block">
              <Card className="shadow-lg border border-violet-200 hover:shadow-xl transition-shadow duration-300 bg-gradient-to-r from-violet-50 to-indigo-50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-violet-600 mb-1">Custom Reports</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Query Builder
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-violet-100">
                      <FiDatabase className="w-6 h-6 text-violet-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>
          </div>
        </div>

        {/* System Administration */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">System Administration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a href="/admin/downloads" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Downloads</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Export Data & Reports
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-green-100">
                      <FiDownload className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a href="/admin/rostering" className="block">
              <Card className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">User Management</p>
                      <p className="text-lg font-semibold text-gray-900">
                        User Rostering
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-blue-100">
                      <FiUserPlus className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;